/**
 * Air Quality Store - Real-time AQ data & Neural Metrics
 */

import { create } from 'zustand';
import type {
  AirQualityData,
  PollutantReading,
  MolecularPermissionLevel,
  AtmosphericLatencyLevel,
  AirQualityForecast,
} from '@/types';
import { calculateNeuralMetrics } from '@/utils/calculations/neuralIndex';

interface AirQualityState {
  // Current readings
  currentReading: AirQualityData | null;
  pm25: PollutantReading | null;
  ozone: PollutantReading | null;
  co2: PollutantReading | null;

  // Neural metrics (calculated)
  neuralIndex: number;
  cognitiveDebt: number;
  molecularPermission: MolecularPermissionLevel;
  atmosphericLatency: AtmosphericLatencyLevel;
  recoveryLatency: number;

  // Forecast data
  forecast: AirQualityForecast[];

  // Loading/error states
  lastUpdated: number | null;
  isLoading: boolean;
  error: Error | null;

  // Actions
  setAirQuality: (data: AirQualityData) => void;
  setForecast: (forecast: AirQualityForecast[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Error | null) => void;
  reset: () => void;
}

const initialMetrics = {
  neuralIndex: 100,
  cognitiveDebt: 0,
  molecularPermission: 'GRANTED' as MolecularPermissionLevel,
  atmosphericLatency: 'LOW' as AtmosphericLatencyLevel,
  recoveryLatency: 0,
};

export const useAirQualityStore = create<AirQualityState>()((set) => ({
  currentReading: null,
  pm25: null,
  ozone: null,
  co2: null,
  ...initialMetrics,
  forecast: [],
  lastUpdated: null,
  isLoading: false,
  error: null,

  setAirQuality: (data) => {
    const metrics = calculateNeuralMetrics(data);

    set({
      currentReading: data,
      pm25: data.pm25,
      ozone: data.ozone,
      co2: data.co2,
      ...metrics,
      lastUpdated: Date.now(),
      error: null,
      isLoading: false,
    });
  },

  setForecast: (forecast) => set({ forecast }),

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) =>
    set({
      error,
      isLoading: false,
    }),

  reset: () =>
    set({
      currentReading: null,
      pm25: null,
      ozone: null,
      co2: null,
      ...initialMetrics,
      forecast: [],
      lastUpdated: null,
      isLoading: false,
      error: null,
    }),
}));

// Selectors for optimized re-renders
export const selectNeuralIndex = (state: AirQualityState) => state.neuralIndex;
export const selectCognitiveDebt = (state: AirQualityState) => state.cognitiveDebt;
export const selectMolecularPermission = (state: AirQualityState) =>
  state.molecularPermission;
export const selectAtmosphericLatency = (state: AirQualityState) =>
  state.atmosphericLatency;
export const selectPM25 = (state: AirQualityState) => state.pm25;
export const selectOzone = (state: AirQualityState) => state.ozone;
export const selectCO2 = (state: AirQualityState) => state.co2;
export const selectForecast = (state: AirQualityState) => state.forecast;
